import {
    Box,
    IconButton,
    TextField,
    Typography,
    styled,
    MenuItem,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import { Remove } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { isValidPrd } from 'src/utils/productKeyValidation';
import { MultiSelect } from '../MultiSelect';
import { ChipsInput } from '../ChipsInput';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetFilterRequest = Models.SetsPlayground.SetFilterRequest;

type ProductOptions = Record<string, string[]>;
type Row = { id: string; key: string; values: string[] };

export interface ProductFilterWidgetProps {
    index?: number;
    set?: SetDefinition;
    filter?: SetFilterRequest;
    onChangeProduct: (index: number | undefined, productOptions: ProductOptions) => void;
    productKey?: string;
    onChangeProductKey?: (key: string) => void;
}

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 2fr auto',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    height: '100%',
    position: 'absolute',
    right: theme.spacing(8),
    top: `calc(50% - ${theme.spacing(4)})`,
}));

const StyledTextField = styled(TextField)(({ theme, value }) => ({
    '& .MuiInputBase-root': {
        backgroundColor: value ? theme.palette.custom.lightBlue : 'transparent',
    },
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const uid = (): string => uuidv4();

const rowsFromProduct = (product?: { productOptions?: Record<string, string[] | string> }): Row[] => {
    const rows: Row[] = [];
    const opts = product?.productOptions ?? {};

    Object.keys(opts).forEach((k) => {
        const raw = opts[k];
        let arr: string[] = [];

        if (Array.isArray(raw)) {
            arr = raw;
        } else if (raw) {
            arr = [raw];
        }

        rows.push({ id: uid(), key: k, values: arr });
    });
    return rows;
};

const rowsToProductOptions = (rows: Row[]): ProductOptions => {
    const out: ProductOptions = {};

    rows.forEach((r) => {
        const key = r.key.trim();

        if (!key || r.values.length === 0) return;

        out[key] = r.values;
    });
    return out;
};

const isComplete = (r: Row): boolean => r.key.trim().length > 0 && r.values.length > 0;
const isEmptyRow = (r: Row): boolean => (!r.key || r.key.trim() === '') && (r.values?.length ?? 0) === 0;

const addEmptyRowIfNeeded = (list: Row[]): Row[] => {
    if (!list.length) {
        return [{ id: uid(), key: '', values: [] }];
    }

    const last = list[list.length - 1];
    const hasEmptyRow = isEmptyRow(last);
    const allComplete = list.every(isComplete);
    const hasIncompleteBeforeLast = list.slice(0, -1).some((r) => !isComplete(r));

    if (hasEmptyRow && hasIncompleteBeforeLast) {
        return list.slice(0, -1);
    }

    if (!hasEmptyRow && allComplete) {
        return [...list, { id: uid(), key: '', values: [] }];
    }
    return list;
};

export const ProductFilterWidget = ({
    index,
    set,
    onChangeProduct,
    filter,
    productKey,
    onChangeProductKey,
}: ProductFilterWidgetProps): JSX.Element => {
    const filterToUse = filter ?? set?.filters;
    const [rows, setRows] = useState<Row[]>(() => addEmptyRowIfNeeded(rowsFromProduct(filterToUse?.product)));
    const [selectedOptionValues, setSelectedOptionValues] = useState<Record<string, string[]>>({});

    useEffect(() => {
        const incomingProduct = filterToUse?.product;
        const currentProduct = rowsToProductOptions(rows);

        if (JSON.stringify(incomingProduct ?? {}) !== JSON.stringify(currentProduct)) {
            setRows(addEmptyRowIfNeeded(rowsFromProduct(incomingProduct)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterToUse?.content]);

    const accessToken = useRecoilValue(isAuthorizedQuery);

    const prdCandidate = (productKey ?? '').trim();
    const prdIsValid = isValidPrd(prdCandidate);

    const { data: productResp, isLoading } = useQueryProductVersion(
        accessToken,
        { productKey: prdCandidate, version: undefined },
        { enabled: prdIsValid, suspense: false },
    );

    const product = productResp?.result?.[0];
    const productOptions = product?.options ?? [];

    const loadOptions = (key: string): void => {
        setSelectedOptionValues((prev) => ({
            ...prev,
            [key]: productOptions.find((opt) => opt.name === key)?.values ?? [],
        }));
    };

    useEffect(() => {
        rows.forEach((row) => {
            loadOptions(row.key);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productOptions]);

    const apply = (nextRows: Row[]): void => {
        const normalized = addEmptyRowIfNeeded(nextRows);

        setRows(normalized);
        onChangeProduct(index, rowsToProductOptions(normalized));
    };

    const updateRowKey = (id: string, key: string): void => {
        const selectedRowIndex = rows.findIndex((r: Row) => r.id === id);
        const normalized = key.trim().toLowerCase();

        if (
            productOptions.length > 0
            && rows.some((r: Row, i: number) => i !== selectedRowIndex && r.key.trim().toLowerCase() === normalized)
        ) {
            return;
        }

        const next = rows.map((r) => (r.id === id ? { ...r, key } : r));

        // Update product option values
        loadOptions(key);

        apply(next);
    };

    const updateRowValues = (id: string, values: string[]): void => {
        const next = rows.map((r) => (r.id === id ? { ...r, values } : r));

        apply(next);
    };

    const removeRow = (id: string): void => {
        const next = rows.filter((r) => r.id !== id);

        apply(next.length ? next : [{ id: uid(), key: '', values: [] }]);
    };

    return (
        <Box mt={2}>
            <Typography gutterBottom fontWeight="medium" sx={{ mb: 3 }} variant="body2">
                Product Filters
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }} variant="body2">
                Add filters based on product options.
                <br />
                Enter a Product Key to load its available options, or leave it blank to define options manually.
            </Typography>
            <RowGrid>
                <StyledTextField
                    error={!isLoading && prdIsValid && !product}
                    helperText={!isLoading && prdIsValid && !product ? 'No product found for this Product Key.' : ''}
                    InputProps={{
                        endAdornment: (
                            <StyledInputAdornment position="end">
                                {prdIsValid && isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            </StyledInputAdornment>
                        ),
                    }}
                    label="Product Key (Optional)"
                    placeholder="PRD-XXXXXXX"
                    size="small"
                    sx={{ mb: 3 }}
                    value={productKey ?? ''}
                    onChange={(e): void => {
                        onChangeProductKey?.(e.target.value);
                    }}
                />
            </RowGrid>
            {rows.map((r: Row, idx: number) => {
                const usedOptions = new Set<string>(
                    rows
                        .filter((_, i: number) => i !== idx)
                        .map((x: Row) => x.key.trim().toLowerCase())
                        .filter(Boolean),
                );

                return (
                    <RowGrid key={r.id}>
                        {productOptions.length > 0 ? (
                            <>
                                <TextField
                                    select
                                    disabled={isLoading}
                                    label="Product Option Name"
                                    size="small"
                                    value={r.key}
                                    onChange={(e): void => updateRowKey(r.id, e.target.value)}
                                >
                                    {productOptions.map((opt: any) => {
                                        const optKey = String(opt.name);
                                        const taken = usedOptions.has(optKey.trim().toLowerCase());

                                        return (
                                            <MenuItem disabled={taken} key={optKey} value={optKey}>
                                                {optKey}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                                <MultiSelect
                                    key={`${r.key}`}
                                    label="Product Option Values"
                                    options={selectedOptionValues[r.key] ?? []}
                                    values={r.values}
                                    onChange={(vals): void => updateRowValues(r.id, vals)}
                                />
                            </>
                        ) : (
                            <>
                                <TextField
                                    label="Product Option Name"
                                    placeholder="e.g., Size"
                                    size="small"
                                    value={r.key}
                                    onChange={(e): void => updateRowKey(r.id, e.target.value)}
                                />
                                <ChipsInput
                                    label="Product Option Values"
                                    values={r.values}
                                    onChange={(vals): void => updateRowValues(r.id, vals)}
                                />
                            </>
                        )}
                        <IconButton
                            aria-label="remove row"
                            disabled={rows.length <= 1 && idx === rows.length - 1}
                            size="small"
                            onClick={(): void => removeRow(r.id)}
                        >
                            <Remove />
                        </IconButton>
                    </RowGrid>
                );
            })}
        </Box>
    );
};
